// src/app/api/adminExtApi.ts
//
// RTK Query endpoints for:
//   • Admin Parents   — GET /admin/parents, GET /admin/parents/:id, PATCH /admin/parents/:id
//   • Admin Showcases — full CRUD + publish/unpublish/cancel/participants
//   • Admin Curriculum — read-only curriculum tree

import { baseApi } from "./baseApi";
import type { StudentTrack } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   SHARED
───────────────────────────────────────────────────────────────── */
interface PagedResponse<T> {
  success: boolean;
  message: string;
  count: number;
  total: number;
  page: number;
  pages: number;
  data: T[];
}
interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/* ─────────────────────────────────────────────────────────────────
   PARENTS
───────────────────────────────────────────────────────────────── */
export interface AdminParent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  avatar: string | null;
  isEmailVerified: boolean;
  childCount: number;
  tracks: StudentTrack[];
  activeEnrolments: number;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminParentEnrolment {
  id: string;
  quarter: string;
  academicYear: string;
  billingPeriod: string;
  billingLabel: string | null;
  status: string;
  paymentStatus: string;
  feeAmount: number;
  coverageEndDate: string | null;
  coverageEndLabel: string | null;
  createdAt: string;
}

export interface AdminParentChild {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  track: StudentTrack;
  studentStatus: string;
  avatar: string | null;
  enrolments: AdminParentEnrolment[];
}

export interface AdminParentDetail extends AdminParent {
  children: AdminParentChild[];
  recentPayments: Array<{
    id: string;
    description: string;
    amount: number;
    amountFormatted: string;
    status: string;
    paidAt: string | null;
    createdAt: string;
  }>;
  totalPaid: number;
  totalPaidFormatted: string;
}

export interface GetAdminParentsParams {
  search?: string;
  page?: number;
  limit?: number;
}

/* ─────────────────────────────────────────────────────────────────
   SHOWCASES
───────────────────────────────────────────────────────────────── */
export type ShowcaseFormat = "in_person" | "online" | "hybrid" | "other";
export type ShowcaseTrack = StudentTrack | "all";

export interface AdminShowcase {
  id: string;
  name: string;
  description: string | null;
  track: ShowcaseTrack;
  quarter: string | null;
  stage: string | null;
  academicYear: string;
  format: ShowcaseFormat;
  scheduledAt: string;
  dateLabel: string | null;
  location: string | null;
  durationMinutes: number;
  externalJudges: boolean;
  parentsInvited: boolean;
  isPublished: boolean;
  isCancelled: boolean;
  daysLeft: number | null;
  participantIds: string[];
  participants: Array<{
    id: string;
    firstName: string;
    lastName: string;
    age: number;
    track: StudentTrack;
    avatar: string | null;
  }>;
  participantCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetAdminShowcasesParams {
  track?: string;
  quarter?: string;
  academicYear?: string;
  isPublished?: boolean;
  isCancelled?: boolean;
}

export interface CreateShowcaseBody {
  name: string;
  description?: string;
  track: string;
  quarter?: string;
  stage?: string;
  academicYear: string;
  format?: ShowcaseFormat;
  scheduledAt: string; // ISO date
  dateLabel?: string;
  location?: string;
  durationMinutes?: number;
  externalJudges?: boolean;
  parentsInvited?: boolean;
}

export type UpdateShowcaseBody = Partial<CreateShowcaseBody>;

/* ─────────────────────────────────────────────────────────────────
   CURRICULUM
───────────────────────────────────────────────────────────────── */
export interface CurriculumModule {
  week: string;
  title: string;
  strand: string;
  topics: string[];
}

export interface CrossCuttingHighlight {
  strand: string;
  topic: string;
}

export interface CurriculumStage {
  id: string;
  label: string;
  quarter: string; // "Q1" | "Q2" | "Q3" | "Stage 1" etc.
  weeks: string; // e.g. "Weeks 1–12" or "12 weeks"
  description: string;
  project: string;
  showcaseFormat: string;
  formationGoal?: string;
  graduationMilestone?: string;
  skills: string[];
  modules: CurriculumModule[];
  crossCuttingHighlights: CrossCuttingHighlight[];
}

export interface CurriculumTrack {
  track: string;
  label: string;
  ageRange: string;
  tagline: string;
  overview: string;
  sessionLength: number;
  sessionsPerQuarter: number | null;
  strands: string[];
  stages: CurriculumStage[];
}

export interface CurriculumSummary {
  track: string;
  label: string;
  ageRange: string;
  tagline: string;
  overview: string;
  sessionLength: number;
  sessionsPerQuarter: number | null;
  stageCount: number;
  strands: string[];
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */
export const adminExtApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /* ══ PARENTS ═══════════════════════════════════════════════════ */

    adminGetParents: builder.query<
      PagedResponse<AdminParent>,
      GetAdminParentsParams
    >({
      query: (params = {}) => ({ url: "/admin/parents", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "AdminParents" as const,
                id,
              })),
              { type: "AdminParents" as const, id: "LIST" },
            ]
          : [{ type: "AdminParents" as const, id: "LIST" }],
    }),

    adminGetParent: builder.query<SingleResponse<AdminParentDetail>, string>({
      query: (id) => `/admin/parents/${id}`,
      providesTags: (_r, _e, id) => [{ type: "AdminParents" as const, id }],
    }),

    adminUpdateParent: builder.mutation<
      SingleResponse<AdminParent>,
      {
        id: string;
        body: Partial<Pick<AdminParent, "firstName" | "lastName" | "phone">>;
      }
    >({
      query: ({ id, body }) => ({
        url: `/admin/parents/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminParents" as const, id },
        { type: "AdminParents" as const, id: "LIST" },
      ],
    }),

    /* ══ SHOWCASES ════════════════════════════════════════════════ */

    adminGetShowcases: builder.query<
      { success: boolean; count: number; data: AdminShowcase[] },
      GetAdminShowcasesParams
    >({
      query: (params = {}) => ({ url: "/admin/showcases", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "AdminShowcases" as const,
                id,
              })),
              { type: "AdminShowcases" as const, id: "LIST" },
            ]
          : [{ type: "AdminShowcases" as const, id: "LIST" }],
    }),

    adminGetShowcase: builder.query<SingleResponse<AdminShowcase>, string>({
      query: (id) => `/admin/showcases/${id}`,
      providesTags: (_r, _e, id) => [{ type: "AdminShowcases" as const, id }],
    }),

    adminCreateShowcase: builder.mutation<
      SingleResponse<AdminShowcase>,
      CreateShowcaseBody
    >({
      query: (body) => ({ url: "/admin/showcases", method: "POST", body }),
      invalidatesTags: [{ type: "AdminShowcases" as const, id: "LIST" }],
    }),

    adminUpdateShowcase: builder.mutation<
      SingleResponse<AdminShowcase>,
      { id: string; body: UpdateShowcaseBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/showcases/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminShowcases" as const, id },
        { type: "AdminShowcases" as const, id: "LIST" },
      ],
    }),

    adminDeleteShowcase: builder.mutation<SingleResponse<null>, string>({
      query: (id) => ({ url: `/admin/showcases/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "AdminShowcases" as const, id: "LIST" }],
    }),

    adminPublishShowcase: builder.mutation<
      SingleResponse<AdminShowcase>,
      string
    >({
      query: (id) => ({
        url: `/admin/showcases/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminShowcases" as const, id },
        { type: "AdminShowcases" as const, id: "LIST" },
      ],
    }),

    adminUnpublishShowcase: builder.mutation<
      SingleResponse<AdminShowcase>,
      string
    >({
      query: (id) => ({
        url: `/admin/showcases/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminShowcases" as const, id },
        { type: "AdminShowcases" as const, id: "LIST" },
      ],
    }),

    adminCancelShowcase: builder.mutation<
      SingleResponse<AdminShowcase>,
      string
    >({
      query: (id) => ({ url: `/admin/showcases/${id}/cancel`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "AdminShowcases" as const, id },
        { type: "AdminShowcases" as const, id: "LIST" },
      ],
    }),

    adminUpdateShowcaseParticipants: builder.mutation<
      SingleResponse<AdminShowcase>,
      { id: string; childIds: string[] }
    >({
      query: ({ id, childIds }) => ({
        url: `/admin/showcases/${id}/participants`,
        method: "PATCH",
        body: { childIds },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AdminShowcases" as const, id },
      ],
    }),

    /* ══ CURRICULUM ═══════════════════════════════════════════════ */

    adminGetCurriculum: builder.query<
      { success: boolean; data: CurriculumSummary[] },
      void
    >({
      query: () => "/admin/curriculum",
      providesTags: [{ type: "AdminCurriculum" as const, id: "LIST" }],
    }),

    adminGetCurriculumTrack: builder.query<
      SingleResponse<CurriculumTrack>,
      string
    >({
      query: (track) => `/admin/curriculum/${track}`,
      providesTags: (_r, _e, track) => [
        { type: "AdminCurriculum" as const, id: track },
      ],
    }),
  }),
});

export const {
  // Parents
  useAdminGetParentsQuery,
  useAdminGetParentQuery,
  useAdminUpdateParentMutation,
  // Showcases
  useAdminGetShowcasesQuery,
  useAdminGetShowcaseQuery,
  useAdminCreateShowcaseMutation,
  useAdminUpdateShowcaseMutation,
  useAdminDeleteShowcaseMutation,
  useAdminPublishShowcaseMutation,
  useAdminUnpublishShowcaseMutation,
  useAdminCancelShowcaseMutation,
  useAdminUpdateShowcaseParticipantsMutation,
  // Curriculum
  useAdminGetCurriculumQuery,
  useAdminGetCurriculumTrackQuery,
} = adminExtApi;
